/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Music, Clock, BarChart3 } from 'lucide-react';
import { TimelineEntry } from '../../types/jamTrack';

interface ChordTimelineProps {
  timeline: TimelineEntry[];
  currentChord: TimelineEntry | null;
  currentTime: number;
  bpm?: number;
  onChordClick?: (chord: TimelineEntry) => void;
}

const ChordTimeline: React.FC<ChordTimelineProps> = ({
  timeline,
  currentChord,
  currentTime,
  bpm,
  onChordClick
}) => {
  const timelineGridRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getChordColor = (chord: TimelineEntry): string => {
    const isActive = currentChord?.chordInstance === chord.chordInstance;
    
    if (isActive) {
      return 'bg-primary text-primary-foreground border-primary';
    }
    
    const isPassed = currentTime > chord.endTime;
    if (isPassed) {
      return 'bg-muted text-muted-foreground border-muted';
    }
    
    return 'bg-secondary text-secondary-foreground border-secondary hover:bg-secondary/80';
  };

  const getProgressPercentage = (): number => {
    if (!currentChord) return 0;
    const duration = currentChord.endTime - currentChord.startTime;
    const elapsed = currentTime - currentChord.startTime;
    return Math.max(0, Math.min(100, (elapsed / duration) * 100));
  };

  // Auto-scroll to keep current chord and upcoming chords visible (desktop only)
  useEffect(() => {
    if (isMobile || !currentChord || !timelineGridRef.current) return;

    const currentChordElement = timelineGridRef.current.querySelector(
      `[data-chord-instance="${currentChord.chordInstance}"]`
    );

    if (currentChordElement) {
      // Scroll within the timeline container only, not the whole page
      currentChordElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }, [currentChord, isMobile]);

  // Get upcoming chords for mobile view
  const getUpcomingChords = (): TimelineEntry[] => {
    if (!currentChord) return timeline.slice(0, 3);

    const currentIndex = timeline.findIndex(
      chord => chord.chordInstance === currentChord.chordInstance
    );

    if (currentIndex === -1) return timeline.slice(0, 3);

    // Get next 3 chords after current
    return timeline.slice(currentIndex + 1, currentIndex + 4);
  };

  return (
    <div className="h-full flex flex-col bg-card rounded-lg border">
      {/* Static Header with Title and Current Chord - ALWAYS VISIBLE */}
      <div className={`flex-shrink-0 ${isMobile ? "p-3" : "p-4"} border-b`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`flex items-center gap-2 font-semibold ${isMobile ? 'text-base' : 'text-lg'}`}>
            <Music className={isMobile ? "h-4 w-4" : "h-5 w-5"} />
            Timeline
          </h3>
          
          {bpm && (
            <div className={`flex items-center gap-1 text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>
              <BarChart3 className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
              <span>{bpm} BPM</span>
            </div>
          )}
        </div>

      </div>

      {isMobile ? (
        /* Mobile: Horizontal Compact Layout */
        <div className="flex-1 p-2 flex items-center gap-2 overflow-x-auto">
          {/* Current Chord */}
          {currentChord ? (
            <div className="flex-shrink-0 bg-primary text-primary-foreground rounded px-3 py-2 border border-primary">
              <div className="text-xs opacity-80">Now</div>
              <div className="text-lg font-bold whitespace-nowrap">{currentChord.chord}</div>
            </div>
          ) : (
            <div className="flex-shrink-0 bg-muted text-muted-foreground rounded px-3 py-2 border border-muted">
              <div className="text-xs">Now</div>
              <div className="text-lg font-bold">—</div>
            </div>
          )}

          {/* Arrow */}
          <div className="text-muted-foreground flex-shrink-0">→</div>

          {/* Upcoming Chords */}
          {getUpcomingChords().map((chord, index) => (
            <div
              key={chord.chordInstance}
              className="flex-shrink-0 bg-secondary text-secondary-foreground rounded px-3 py-2 border border-secondary"
            >
              <div className="text-xs opacity-70">Next</div>
              <div className="text-base font-semibold whitespace-nowrap">{chord.chord}</div>
            </div>
          ))}
          {getUpcomingChords().length === 0 && (
            <div className="text-sm text-muted-foreground italic flex-shrink-0">End</div>
          )}
        </div>
      ) : (
        /* Desktop: Full Grid View */
        <>
          <div className="flex-shrink-0 px-4 pt-3">
            <h4 className="font-medium text-muted-foreground text-sm">Chord Progression</h4>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            <div
              ref={timelineGridRef}
              className="grid overflow-y-auto scrollbar-hide h-full grid-cols-3 gap-3"
            >
              {timeline.map((chord) => (
                <div
                  key={chord.chordInstance}
                  data-chord-instance={chord.chordInstance}
                  className={`rounded-lg border cursor-pointer transition-all duration-200 ${getChordColor(chord)} p-3 h-fit min-h-[60px] flex items-center justify-center`}
                  onClick={() => onChordClick?.(chord)}
                >
                  <div className="text-center">
                    <div className="font-semibold text-sm mb-1">{chord.chord}</div>
                    <div className="text-xs opacity-80">{formatTime(chord.startTime)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Fixed Stats at Bottom */}
      {!isMobile && (
        <div className="flex-shrink-0 flex items-center justify-between p-4 pt-2 border-t text-xs text-muted-foreground">
          <span>{timeline.length} chords</span>
          <span>
            {timeline.length > 0 && formatTime(timeline[timeline.length - 1].endTime)} total
          </span>
        </div>
      )}
    </div>
  );
};

export default ChordTimeline;